/**
 * Contas a Receber — recebíveis avulsos, nota da Inttrix, recebimento e crédito do pagador (mecânica simplificada
 * aprovada em 23/10/2026).
 *  - Recebível avulso: cadastro só com o necessário (pagador, tipo, descrição, valor, situação, previsão, origem);
 *    sem oportunidade, proposta, pedido ou ordem. Ficha com previsto, confirmado, recebido e saldo.
 *  - Nota da Inttrix: reúne itens disponíveis para resgate (liberados e confirmados) e avulsos confirmados do mesmo
 *    pagador; IR destacado. Reemissão e cancelamento justificados; carta de correção anexada.
 *  - Registrar recebimento: conta, data, valor, identificador e comprovante; descontos (IR, antecipação) opcionais;
 *    o efeito aparece antes de salvar. Fica aguardando extrato: a conciliação futura só vincula.
 *  - Créditos do pagador: saldo, aplicar, devolver e desfazer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { Ajuda, Codigo, ComCodigos, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga } from '../comercial/ui.tsx';
import { CampoData, hojeIso } from '../comercial/CampoData.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { centavosDe, dataBr, novaChave, rs } from './ContasPagar.tsx';

const cod = (t: string | null | undefined): ReactNode => {
  const x = t ?? '';
  if (!x.includes('-')) return x;
  return x.split(/(\s+)/).map((parte, k) => (parte.includes('-') ? <span key={k} className="rc-cod">{parte}</span> : parte));
};

// ---------------------------------------------------------------- tipos
export interface PermissoesReceber {
  registrarAvulso: boolean;
  cancelarAvulso: boolean;
  emitirNota: boolean;
  cancelarNota: boolean;
  registrarRecebimento: boolean;
  estornarRecebimento: boolean;
  gerirCredito: boolean;
  enviarDocumento: boolean;
}
interface Pagador {
  tipo: 'parceiro' | 'pessoa';
  id: string;
  nome: string;
  documento: string | null;
}
interface Anexo {
  id: string;
  papel: string;
  nome: string;
  em: string;
  por: string | null;
  retirado: { em: string; motivo: string } | null;
}
interface Recebimento {
  id: string;
  numero: string;
  conta: { id: string; nome: string };
  data: string;
  valor: string;
  ir: { percentual: string; base: string; valor: string } | null;
  antecipacao: { tipo: 'valor' | 'percentual'; percentual: string | null; valor: string } | null;
  identificador: string | null;
  credito: { numero: string; valor: string } | null;
  registradoEm: string;
  por: string | null;
  estorno: { justificativa: string; em: string; por: string | null } | null;
  anexos: Anexo[];
  conciliacao: 'conciliado' | 'aguardando_extrato';
  conciliacaoNumero: string | null;
  conciliacaoMovimentos: string | null;
  registro: string | null;
}
interface Movimento {
  id: string;
  credito: string;
  tipo: 'aplicacao' | 'devolucao';
  alvo: string | null;
  conta: string | null;
  data: string;
  valor: string;
  desfeito: { justificativa: string; em: string } | null;
  em: string;
  por: string | null;
}
interface Avulso {
  id: string;
  numero: string;
  tipo: string;
  tipoRotulo: string;
  pagador: Pagador | null;
  pagadorEntidadeId: string | null;
  pagadorPessoaId: string | null;
  descricao: string;
  origem: string;
  observacao: string | null;
  planoConta: string | null;
  valor: string;
  situacaoValor: 'estimado' | 'confirmado';
  previsao: string;
  valores: { valor: string; situacao: string; previsao: string; justificativa: string | null; em: string; por: string | null }[];
  cancelamento: { justificativa: string; em: string; por: string | null } | null;
  notas: { id: string; numero: string; numeroFiscal: string; situacao: string; valor: string; recebido: string }[];
  recebimentos: Recebimento[];
  creditos: Movimento[];
  recebido: string;
  descontos: { ir: string; antecipacao: string };
  creditoAplicado: string;
  quitado: string;
  saldo: string;
  cobrado: string;
  situacao: 'a_receber' | 'parcial' | 'recebido' | 'cancelado';
  criadoEm: string;
  criadoPor: string | null;
  anexos: Anexo[];
  permissoes: PermissoesReceber;
}
interface Nota {
  id: string;
  numero: string;
  numeroFiscal: string;
  dataEmissao: string;
  valor: string;
  ir: { percentual: string; base: string; valor: string } | null;
  liquidoEsperado: string;
  pagador: Pagador | null;
  substitui: { numero: string } | null;
  substituta: { numero: string } | null;
  cancelamento: { motivo: string; tratamento: 'sem_recebimento' | 'credito' | 'devolucao'; justificativa: string; data: string; em: string; por: string | null } | null;
  liberacaoItens: { justificativa: string; em: string; por: string | null } | null;
  seguraItens: boolean;
  creditoCancelamento: { numero: string; valor: string; saldo: string } | null;
  situacao: 'aberta' | 'parcial' | 'quitada' | 'cancelada' | 'substituida';
  itens: { id: string; origem: string; rotulo: string; valor: string; quitado: string; recebidoCaixa: string }[];
  recebimentos: Recebimento[];
  creditos: Movimento[];
  recebido: string;
  descontos: { ir: string; antecipacao: string };
  creditoAplicado: string;
  quitado: string;
  saldo: string;
  observacao: string | null;
  registradaEm: string;
  por: string | null;
  anexos: Anexo[];
  creditosDisponiveis: { numero: string; saldo: string }[];
  permissoes: PermissoesReceber;
}
interface Apoio {
  contas: { id: string; nome: string; principal: boolean }[];
  classificacoes: { id: string; nome: string }[];
  tipos: { id: string; rotulo: string }[];
  hoje: string;
}

// ---------------------------------------------------------------- utilidades
const SIT_NOTA: Record<Nota['situacao'], { rotulo: string; tom: string }> = { aberta: { rotulo: 'A receber', tom: 'roxo' }, parcial: { rotulo: 'Recebida parcialmente', tom: 'amarelo' }, quitada: { rotulo: 'Quitada', tom: 'verde' }, cancelada: { rotulo: 'Cancelada', tom: 'cinza' }, substituida: { rotulo: 'Substituída (reemitida)', tom: 'cinza' } };
const SIT_AVULSO: Record<Avulso['situacao'], { rotulo: string; tom: string }> = { a_receber: { rotulo: 'A receber', tom: 'roxo' }, parcial: { rotulo: 'Recebido parcialmente', tom: 'amarelo' }, recebido: { rotulo: 'Recebido', tom: 'verde' }, cancelado: { rotulo: 'Cancelado', tom: 'cinza' } };
export const Situacao = ({ rotulo, tom, teste }: { rotulo: string; tom: string; teste?: string }) => (
  <span className={`rc-sit ${tom}`} data-teste={teste}>
    {rotulo}
  </span>
);
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const paraBase64 = (b: Uint8Array) => {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
};
const lerArquivo = async (f: File | null | undefined) => (f ? { nome: f.name, conteudo_base64: paraBase64(new Uint8Array(await f.arrayBuffer())) } : undefined);
async function baixar(id: string) {
  const r = await api.get<{ nome: string; conteudo_base64: string }>(`/api/receber/anexos/${id}/arquivo`);
  const bytes = Uint8Array.from(atob(r.conteudo_base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
  const el = document.createElement('a');
  el.href = url;
  el.download = r.nome;
  el.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const Erro = ({ texto }: { texto: string | null }) => (texto ? <div className="aviso aviso-erro" role="alert">{texto}</div> : null);
const Arquivos = ({ anexos }: { anexos: Anexo[] }) => (
  <>
    {anexos
      .filter((a) => !a.retirado)
      .map((a) => (
        <button key={a.id} type="button" className="botao-link rc-arquivo" onClick={() => void baixar(a.id)} title={`${a.papel === 'carta_correcao' ? 'Carta de correção' : a.papel === 'comprovante' ? 'Comprovante' : 'Documento'} · enviado em ${dataHora(a.em)}`}>
          {a.nome}
        </button>
      ))}
  </>
);
function useEnviar<T = unknown>(aoSalvar: (r: T) => void) {
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enviar = async (metodo: 'post' | 'patch', caminho: string, corpo: Record<string, unknown>) => {
    setEnviando(true);
    setErro(null);
    try {
      const r = metodo === 'post' ? await api.post<T>(caminho, corpo) : await api.patch<T>(caminho, corpo);
      aoSalvar(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return { erro, enviando, enviar };
}
/** Prévia ao vivo (a mesma escrita, desfeita no servidor): mostra o efeito antes de salvar, sem confirmação extra. */
function usePrevia<T>(caminho: string, corpo: Record<string, unknown> | null) {
  const [previa, setPrevia] = useState<T | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const chave = corpo ? JSON.stringify(corpo) : '';
  useEffect(() => {
    if (!corpo) {
      setPrevia(null);
      setErro(null);
      return;
    }
    let vivo = true;
    const t = setTimeout(() => {
      api
        .post<T>(caminho, { ...corpo, previa: true })
        .then((r) => vivo && (setPrevia(r), setErro(null)))
        .catch((e) => vivo && (setPrevia(null), setErro((e as Error).message)));
    }, 300);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caminho, chave]);
  return { previa, erro };
}
const Rodape = ({ aoFechar, rotulo, pronto, enviando, aoConfirmar, teste, antes }: { aoFechar: () => void; rotulo: string; pronto: boolean; enviando: boolean; aoConfirmar: () => void; teste: string; antes?: ReactNode }) => (
  <>
    {antes}
    <button type="button" className="botao-secundario" onClick={aoFechar}>
      Cancelar
    </button>
    <button type="button" className="botao-primario" disabled={!pronto || enviando} aria-busy={enviando} onClick={aoConfirmar} data-teste={teste}>
      {rotulo}
    </button>
  </>
);
function useApoio() {
  const [a, setA] = useState<Apoio | null>(null);
  useEffect(() => {
    void api.get<Apoio>('/api/receber/apoio').then(setA);
  }, []);
  return a;
}

// ---------------------------------------------------------------- pagador
function BuscaPagador({ valor, aoEscolher }: { valor: Pagador | null; aoEscolher: (p: Pagador | null) => void }) {
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<Pagador[]>([]);
  useEffect(() => {
    if (busca.trim().length < 2) {
      setLista([]);
      return;
    }
    let vivo = true;
    const t = setTimeout(() => void api.get<Pagador[]>(`/api/receber/pagadores?busca=${encodeURIComponent(busca.trim())}`).then((r) => vivo && setLista(r)), 250);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [busca]);
  if (valor)
    return (
      <div className="rc-pagador-escolhido" data-teste="pagador-escolhido">
        <span>
          <strong>{valor.nome}</strong>
          {valor.documento && <span className="rc-sec">{valor.documento}</span>}
        </span>
        <button type="button" className="botao-link" onClick={() => aoEscolher(null)}>
          trocar
        </button>
      </div>
    );
  return (
    <div className="rc-busca-pagador">
      <input value={busca} placeholder="Buscar parceiro, CNPJ ou pessoa" onChange={(e) => setBusca(e.target.value)} data-teste="busca-pagador" aria-label="Pagador" />
      {lista.length > 0 && (
        <ul className="rc-sugestoes" role="listbox">
          {lista.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => (aoEscolher(p), setBusca(''))} data-teste="opcao-pagador">
                <strong>{p.nome}</strong>
                {p.documento && <span className="rc-sec">{p.documento}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- novo recebível avulso
export function NovoAvulso({ aoFechar, aoSalvar }: { aoFechar: () => void; aoSalvar: (numero: string) => void }) {
  const apoio = useApoio();
  const [pagador, setPagador] = useState<Pagador | null>(null);
  const [tipo, setTipo] = useState('verba');
  const [tipoOutro, setTipoOutro] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [situacao, setSituacao] = useState<'estimado' | 'confirmado'>('estimado');
  const [previsao, setPrevisao] = useState('');
  const [origem, setOrigem] = useState('');
  const [observacao, setObservacao] = useState('');
  const [plano, setPlano] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [chave] = useState(novaChave);
  const env = useEnviar<{ numero: string }>((r) => aoSalvar(r.numero));
  const pronto = Boolean(pagador && descricao.trim().length >= 3 && centavosDe(valor) > 0 && previsao && origem.trim().length >= 3 && (tipo !== 'outro' || tipoOutro.trim().length >= 3));
  const salvar = async () =>
    env.enviar('post', '/api/receber/avulsos', { tipo, tipo_outro: tipo === 'outro' ? tipoOutro : undefined, pagador: pagador && { tipo: pagador.tipo, id: pagador.id }, descricao, valor: (centavosDe(valor) / 100).toFixed(2), situacao, previsao, origem, observacao: observacao || undefined, plano_conta_id: plano || undefined, documento: await lerArquivo(arquivo), chave_idempotencia: chave });
  return (
    <Modal
      titulo="Novo recebível avulso"
      aoFechar={aoFechar}
      medio
      rodape={
        <Rodape
          aoFechar={aoFechar}
          rotulo="Criar recebível"
          pronto={pronto}
          enviando={env.enviando}
          aoConfirmar={() => void salvar()}
          teste="criar-avulso"
          antes={<span className="rc-info rc-rodape-info">Não registra dinheiro recebido. Nenhum pedido comercial é necessário.</span>}
        />
      }
    >
      <Erro texto={env.erro} />
      <div className="grade-form duas">
        <Campo rotulo="Pagador *">{() => <BuscaPagador valor={pagador} aoEscolher={setPagador} />}</Campo>
        <Campo rotulo="Tipo *">
          {(id) => (
            <select id={id} value={tipo} onChange={(e) => setTipo(e.target.value)} data-teste="tipo-avulso">
              {(apoio?.tipos ?? [{ id: 'verba', rotulo: 'Verba' }]).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id === 'verba' ? 'Verba de parceiro' : t.id === 'outro' ? 'Outro (identifique)' : t.rotulo}
                </option>
              ))}
            </select>
          )}
        </Campo>
        {tipo === 'outro' && <Campo rotulo="Identifique o tipo *">{(id) => <input id={id} value={tipoOutro} maxLength={60} onChange={(e) => setTipoOutro(e.target.value)} />}</Campo>}
        <Campo rotulo="Descrição *">{(id) => <input id={id} value={descricao} maxLength={200} onChange={(e) => setDescricao(e.target.value)} data-teste="descricao-avulso" />}</Campo>
        <Campo rotulo="Valor *">{(id) => <input id={id} inputMode="decimal" value={valor} placeholder="R$ 0,00" onChange={(e) => setValor(e.target.value)} data-teste="valor-avulso" />}</Campo>
        <Campo rotulo="Situação do valor *">
          {(id) => (
            <select id={id} value={situacao} onChange={(e) => setSituacao(e.target.value as 'estimado' | 'confirmado')} data-teste="situacao-avulso">
              <option value="estimado">Estimado</option>
              <option value="confirmado">Confirmado com o pagador</option>
            </select>
          )}
        </Campo>
        <CampoData rotulo="Previsão de recebimento" valor={previsao} aoMudar={setPrevisao} obrigatorio teste="previsao-avulso" />
        <Campo rotulo="Origem *" ajuda="Acordo, campanha ou documento que dá origem ao recebível.">{(id) => <input id={id} value={origem} maxLength={200} placeholder="Ex.: acordo, campanha, contrato" onChange={(e) => setOrigem(e.target.value)} data-teste="origem-avulso" />}</Campo>
        <Campo rotulo="Documento (opcional)">{(id) => <input id={id} type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="documento-avulso" />}</Campo>
      </div>
      <Campo rotulo="Observação (opcional)">{(id) => <textarea id={id} rows={2} maxLength={1000} value={observacao} placeholder="Informações adicionais, se necessário." onChange={(e) => setObservacao(e.target.value)} />}</Campo>
      <details className="rc-recolhido">
        <summary>
          Classificação e documentos adicionais <span className="texto-secundario">(opcional)</span>
        </summary>
        <div>
          <Campo rotulo="Classificação gerencial (receita)">
            {(id) => (
              <select id={id} value={plano} onChange={(e) => setPlano(e.target.value)}>
                <option value="">Sem classificação</option>
                {(apoio?.classificacoes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <p className="texto-secundario sem-margem">Devoluções de fornecedor continuam no Contas a Pagar (crédito do fornecedor). Aportes, empréstimos e transferências não são receita e não entram aqui.</p>
        </div>
      </details>
    </Modal>
  );
}

// ---------------------------------------------------------------- registrar recebimento
interface PreviaRecebimento {
  saldoAntes: string;
  abatido: string;
  ir: string | null;
  antecipacao: string | null;
  saldoDepois: string;
  excedente: string;
  texto: string;
  conciliacao: string;
}
export function RegistrarRecebimento({ alvo, devido, irNota, aoFechar, aoSalvar }: { alvo: { nota?: string; avulso?: string; rotulo: string }; devido: string; irNota?: { percentual: string; base: string } | null; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const [conta, setConta] = useState('');
  const [data, setData] = useState(hojeIso());
  const [valor, setValor] = useState(Number(devido) > 0 ? Number(devido).toFixed(2).replace('.', ',') : '');
  const [identificador, setIdentificador] = useState('');
  const [irPct, setIrPct] = useState(irNota ? irNota.percentual.replace('.', ',') : '');
  const [irBase, setIrBase] = useState(irNota ? irNota.base.replace('.', ',') : '');
  const [antTipo, setAntTipo] = useState<'' | 'valor' | 'percentual'>('');
  const [ant, setAnt] = useState('');
  const [credito, setCredito] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [chave] = useState(novaChave);
  useEffect(() => {
    if (apoio && !conta) setConta(apoio.contas.find((c) => c.principal)?.id ?? apoio.contas[0]?.id ?? '');
  }, [apoio, conta]);
  const cent = centavosDe(valor);
  const corpo =
    cent > 0 && data
      ? {
          ...(alvo.nota ? { nota: alvo.nota } : { avulso: alvo.avulso }),
          conta_bancaria_id: conta || undefined,
          data,
          valor: (cent / 100).toFixed(2),
          identificador: identificador || undefined,
          ir: irPct ? { percentual: irPct, base: irBase || undefined } : undefined,
          antecipacao: antTipo && ant ? (antTipo === 'valor' ? { tipo: 'valor', valor: ant } : { tipo: 'percentual', percentual: ant }) : undefined,
          excedente: credito ? 'credito' : undefined,
        }
      : null;
  const pv = usePrevia<PreviaRecebimento>('/api/receber/recebimentos', corpo);
  const env = useEnviar(() => aoSalvar());
  const excedente = pv.previa ? Number(pv.previa.excedente) : 0;
  const salvar = async () => env.enviar('post', '/api/receber/recebimentos', { ...corpo!, comprovante: await lerArquivo(arquivo), chave_idempotencia: chave });
  return (
    <Modal titulo="Registrar recebimento" aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Registrar recebimento" pronto={Boolean(pv.previa && conta) && (excedente === 0 || credito)} enviando={env.enviando} aoConfirmar={() => void salvar()} teste="confirmar-recebimento" />}>
      <p className="texto-secundario sem-margem">{alvo.rotulo}</p>
      <div className="rc-card destaque" data-teste="valor-devido">
        <b>Valor devido</b>
        <strong>{rs(devido)}</strong>
      </div>
      <Erro texto={env.erro} />
      <Campo rotulo="Conta bancária *">
        {(id) => (
          <select id={id} value={conta} onChange={(e) => setConta(e.target.value)} data-teste="conta-recebimento">
            {(apoio?.contas ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        )}
      </Campo>
      <div className="grade-form duas">
        <CampoData rotulo="Data de recebimento" valor={data} aoMudar={setData} max={hojeIso()} obrigatorio teste="data-recebimento" />
        <Campo rotulo="Valor recebido *">{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} data-teste="valor-recebimento" />}</Campo>
      </div>
      <Campo rotulo="Identificador (opcional)" ajuda="Identificação da transação no banco (TED, PIX…). O mesmo identificador não é aceito duas vezes na mesma conta.">
        {(id) => <input id={id} value={identificador} maxLength={120} onChange={(e) => setIdentificador(e.target.value)} data-teste="identificador-recebimento" />}
      </Campo>
      <details className="rc-recolhido" open={Boolean(irNota)}>
        <summary>
          Descontos informados pelo pagador <span className="texto-secundario">(opcional) · IR ou antecipação, quando houver</span>
        </summary>
        <div className="grade-form quatro">
          <Campo rotulo="IR retido (%)">{(id) => <input id={id} inputMode="decimal" value={irPct} onChange={(e) => setIrPct(e.target.value)} data-teste="ir-percentual" />}</Campo>
          <Campo rotulo="Base do IR">{(id) => <input id={id} inputMode="decimal" value={irBase} placeholder="valor da nota" onChange={(e) => setIrBase(e.target.value)} />}</Campo>
          <Campo rotulo="Antecipação">
            {(id) => (
              <select id={id} value={antTipo} onChange={(e) => (setAntTipo(e.target.value as '' | 'valor' | 'percentual'), setAnt(''))} data-teste="antecipacao-tipo">
                <option value="">Sem antecipação</option>
                <option value="valor">Valor (R$)</option>
                <option value="percentual">Percentual (%)</option>
              </select>
            )}
          </Campo>
          <Campo rotulo={antTipo === 'percentual' ? 'Antecipação (%)' : 'Antecipação (R$)'}>{(id) => <input id={id} inputMode="decimal" disabled={!antTipo} value={ant} onChange={(e) => setAnt(e.target.value)} data-teste="antecipacao-valor" />}</Campo>
        </div>
      </details>
      <Campo rotulo="Comprovante (opcional)">{(id) => <input id={id} type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="comprovante-recebimento" />}</Campo>
      <Erro texto={pv.erro} />
      {pv.previa && (
        <>
          {excedente > 0 && (
            <label className="opcao rc-alerta atencao">
              <input type="checkbox" checked={credito} onChange={(e) => setCredito(e.target.checked)} data-teste="excedente-credito" /> O total passa do valor devido em {rs(pv.previa.excedente)}: registrar o excedente como <strong>crédito do pagador</strong>
            </label>
          )}
          <p className="rc-efeito-salvar" data-teste="efeito-recebimento">
            {pv.previa.texto}
          </p>
        </>
      )}
      <p className="rc-info">Quando o extrato for importado, a conciliação apenas vincula esta entrada. Não gera outro recebimento.</p>
    </Modal>
  );
}

// ---------------------------------------------------------------- emitir nota da Inttrix
interface Disponiveis {
  comissoes: { tipo: 'comissao'; id: string; parceiroId: string; parceiro: string; comissao: string; rotulo: string; origem: string; saldo: string }[];
  avulsos: { tipo: 'avulso'; id: string; numero: string; pagadorEntidadeId: string | null; pagadorPessoaId: string | null; pagador: string; rotulo: string; saldo: string }[];
  entidades: { id: string; parceiroId: string; nome: string; documento: string }[];
  hoje: string;
}
export function EmitirNota({ inicial, aoFechar, aoSalvar }: { inicial?: { parceiroId?: string; comissao?: string; avulso?: string }; aoFechar: () => void; aoSalvar: (numero: string) => void }) {
  const carga = useCarga(() => api.get<Disponiveis>('/api/receber/notas/disponiveis'), []);
  const d = carga.dados;
  const avIni = d?.avulsos.find((a) => a.numero === inicial?.avulso);
  const parceiroIni = inicial?.parceiroId ?? d?.comissoes.find((c) => c.comissao === inicial?.comissao)?.parceiroId ?? (avIni?.pagadorEntidadeId ? d?.entidades.find((e) => e.id === avIni.pagadorEntidadeId)?.parceiroId : undefined);
  const [entidade, setEntidade] = useState('');
  const [marcados, setMarcados] = useState<string[] | null>(null);
  const [numeroFiscal, setNumeroFiscal] = useState('');
  const [data, setData] = useState(hojeIso());
  const [irPct, setIrPct] = useState('');
  const [observacao, setObservacao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [chave] = useState(novaChave);
  useEffect(() => {
    if (!d || marcados !== null) return;
    const ent = avIni?.pagadorEntidadeId ?? d.entidades.find((e) => e.parceiroId === parceiroIni)?.id ?? '';
    setEntidade(ent);
    const inicialIds = [...d.comissoes.filter((c) => (inicial?.comissao ? c.comissao === inicial.comissao : c.parceiroId === parceiroIni)).map((c) => c.id), ...(avIni ? [avIni.id] : [])];
    setMarcados(inicialIds);
  }, [d, marcados, avIni, parceiroIni, inicial?.comissao]);
  const parceiroDaEntidade = d?.entidades.find((e) => e.id === entidade)?.parceiroId;
  const comissoes = (d?.comissoes ?? []).filter((c) => c.parceiroId === parceiroDaEntidade);
  const avulsos = (d?.avulsos ?? []).filter((a) => a.pagadorEntidadeId === entidade);
  const sel = marcados ?? [];
  const itens = [...comissoes.filter((c) => sel.includes(c.id)).map((c) => ({ comissao_item_id: c.id })), ...avulsos.filter((a) => sel.includes(a.id)).map((a) => ({ recebivel_id: a.id }))];
  const total = [...comissoes, ...avulsos].filter((x) => sel.includes(x.id)).reduce((s, x) => s + centavosDe(x.saldo.replace('.', ',')), 0);
  const corpo = entidade && itens.length && data ? { pagador: { tipo: 'parceiro', id: entidade }, numero_fiscal: numeroFiscal || undefined, data_emissao: data, itens, ir: irPct ? { percentual: irPct } : undefined, observacao: observacao || undefined } : null;
  const pv = usePrevia<{ valor: string; ir: { valor: string } | null; liquidoEsperado: string }>('/api/receber/notas', corpo);
  const env = useEnviar<{ numero: string }>((r) => aoSalvar(r.numero));
  const salvar = async () => env.enviar('post', '/api/receber/notas', { ...corpo!, numero_fiscal: numeroFiscal, documento: await lerArquivo(arquivo), chave_idempotencia: chave });
  const entidadesUsaveis = (d?.entidades ?? []).filter((e) => d?.comissoes.some((c) => c.parceiroId === e.parceiroId) || d?.avulsos.some((a) => a.pagadorEntidadeId === e.id));
  return (
    <Modal titulo="Emitir nota da Inttrix" aoFechar={aoFechar} largo rodape={<Rodape aoFechar={aoFechar} rotulo="Registrar nota" pronto={Boolean(pv.previa) && numeroFiscal.trim().length > 0} enviando={env.enviando} aoConfirmar={() => void salvar()} teste="confirmar-nota" />}>
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : (
        <>
          <Erro texto={env.erro} />
          <div className="grade-form tres">
            <Campo rotulo="Pagador (CNPJ do parceiro) *">
              {(id) => (
                <select
                  id={id}
                  value={entidade}
                  onChange={(e) => {
                    // ao trocar o pagador, todos os itens disponíveis dele vêm marcados (pode desmarcar)
                    const ent = e.target.value;
                    const parc = d.entidades.find((x) => x.id === ent)?.parceiroId;
                    setEntidade(ent);
                    setMarcados([...d.comissoes.filter((c) => c.parceiroId === parc).map((c) => c.id), ...d.avulsos.filter((a) => a.pagadorEntidadeId === ent).map((a) => a.id)]);
                  }}
                  data-teste="pagador-nota"
                >
                  <option value="">Escolha</option>
                  {entidadesUsaveis.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome} · {e.documento}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Número da nota fiscal *">{(id) => <input id={id} value={numeroFiscal} maxLength={40} onChange={(e) => setNumeroFiscal(e.target.value)} data-teste="numero-nota" />}</Campo>
            <CampoData rotulo="Data de emissão" valor={data} aoMudar={setData} max={hojeIso()} obrigatorio teste="data-nota" />
          </div>
          <h3 className="rc-subtitulo">
            Itens da nota
            <Ajuda titulo="Itens da nota">Só itens liberados e com valor confirmado (disponíveis para resgate) e recebíveis avulsos confirmados do mesmo pagador. Cada item entra pelo saldo ainda não cobrado: nada é cobrado duas vezes.</Ajuda>
          </h3>
          {comissoes.length + avulsos.length === 0 ? (
            <p className="texto-secundario">Nenhum item disponível para este pagador.</p>
          ) : (
            <RolagemIndicada className="rolagem quadro-tabela" teste="itens-nota">
              <table className="tabela tabela-compacta rc-fixo">
                <thead>
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Item</th>
                    <th>Origem</th>
                    <th className="numero">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {[...comissoes, ...avulsos].map((x) => (
                    <tr key={x.id}>
                      <td>
                        <input type="checkbox" aria-label={x.rotulo} checked={sel.includes(x.id)} onChange={(e) => setMarcados(e.target.checked ? [...sel, x.id] : sel.filter((y) => y !== x.id))} data-teste="item-nota" />
                      </td>
                      <td>{cod(x.rotulo)}</td>
                      <td className="texto-secundario">{x.tipo === 'comissao' ? cod(x.origem) : x.pagador}</td>
                      <td className="numero">{rs(x.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          )}
          <div className="grade-form tres">
            <Campo rotulo="IR destacado (%) (opcional)">{(id) => <input id={id} inputMode="decimal" value={irPct} onChange={(e) => setIrPct(e.target.value)} data-teste="ir-nota" />}</Campo>
            <Campo rotulo="Observação (opcional)">{(id) => <input id={id} value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} />}</Campo>
            <Campo rotulo="Arquivo da nota (opcional)">{(id) => <input id={id} type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />}</Campo>
          </div>
          <Erro texto={pv.erro} />
          <p className="faixa-resumo-modal" data-teste="resumo-nota">
            <span>
              Valor da nota: <strong>{rs(pv.previa?.valor ?? total / 100)}</strong>
            </span>
            <span>IR destacado: {pv.previa?.ir ? rs(pv.previa.ir.valor) : '—'}</span>
            <span>
              Líquido esperado: <strong>{pv.previa ? rs(pv.previa.liquidoEsperado) : '—'}</strong>
            </span>
          </p>
          <p className="rc-info">A receita gerencial é do mês da emissão desta nota, pelo valor da nota (dados gravados para a DRE futura).</p>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- justificativa (ações de consequência relevante)
function Justificar({ titulo, texto, rotulo, caminho, corpoExtra, aoFechar, aoSalvar, teste, filho }: { titulo: string; texto: string; rotulo: string; caminho: string; corpoExtra?: Record<string, unknown>; aoFechar: () => void; aoSalvar: () => void; teste: string; filho?: ReactNode }) {
  const [just, setJust] = useState('');
  const env = useEnviar(() => aoSalvar());
  return (
    <Modal titulo={titulo} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo={rotulo} pronto={just.trim().length >= 10} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', caminho, { ...corpoExtra, justificativa: just })} teste={teste} />}>
      <Erro texto={env.erro} />
      <p>{texto}</p>
      {filho}
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} data-teste={`${teste}-justificativa`} />}</Campo>
    </Modal>
  );
}

function TabelaRecebimentos({ recebimentos, podeEstornar, aoEstornar }: { recebimentos: Recebimento[]; podeEstornar: boolean; aoEstornar: (r: Recebimento) => void }) {
  if (!recebimentos.length) return <p className="texto-secundario">Nenhum recebimento registrado.</p>;
  return (
    <RolagemIndicada className="rolagem quadro-tabela" teste="recebimentos">
      <table className="tabela tabela-compacta rc-fixo" data-teste="tabela-recebimentos">
        <thead>
          <tr>
            <th style={{ width: '16%' }}>Recebimento</th>
            <th style={{ width: '12%' }}>Data efetiva</th>
            <th style={{ width: '15%' }}>Conta</th>
            <th className="numero" style={{ width: '13%' }}>
              Em dinheiro
            </th>
            <th className="rc-direita" style={{ width: '14%' }}>
              Retenções
            </th>
            <th style={{ width: '15%' }}>Conciliação</th>
            <th style={{ width: '15%' }} />
          </tr>
        </thead>
        <tbody>
          {recebimentos.map((r) => (
            <tr key={r.id} className={r.estorno ? 'rc-linha-bloq' : undefined}>
              <td>
                <strong>{cod(r.numero)}</strong>
                {r.identificador && <span className="rc-sec rc-identificador"><ComCodigos texto={r.identificador} /></span>}
                {r.credito && <span className="rc-sec">excedente → <Codigo>{r.credito.numero}</Codigo> ({rs(r.credito.valor)})</span>}
                <Arquivos anexos={r.anexos} />
              </td>
              <td>{dataBr(r.data)}</td>
              <td className="texto-secundario">{r.conta.nome}</td>
              <td className="numero">{rs(r.valor)}</td>
              <td className="rc-direita">
                {!r.ir && !r.antecipacao && '—'}
                {r.ir && (
                  <span className="rc-l2">
                    IR <span className="rc-valor">{rs(r.ir.valor)}</span>
                  </span>
                )}
                {r.antecipacao && (
                  <span className="rc-l2">
                    antecip. <span className="rc-valor">{rs(r.antecipacao.valor)}</span>
                  </span>
                )}
              </td>
              <td data-teste={`conciliacao-${r.numero}`}>
                {r.estorno ? (
                  <span className="texto-secundario">estornado · {r.estorno.justificativa}</span>
                ) : r.conciliacao === 'conciliado' ? (
                  <span className="rbm-conciliacao">
                    <SeloSituacao texto="Conciliado" tom="positivo" />
                    <span className="rc-sec">
                      {r.conciliacaoNumero}
                      {r.conciliacaoMovimentos ? ` · ${r.conciliacaoMovimentos}` : ''}
                    </span>
                  </span>
                ) : (
                  <span className="rc-sec">Aguardando extrato</span>
                )}
                {r.registro && <span className="rc-sec">registro {r.registro}</span>}
              </td>
              <td>
                {!r.estorno && podeEstornar && r.conciliacao === 'conciliado' && (
                  <span className="rc-sec" data-teste={`estorno-bloqueado-${r.numero}`}>
                    Estorno: desfaça {r.conciliacaoNumero} antes
                  </span>
                )}
                {!r.estorno && podeEstornar && r.conciliacao !== 'conciliado' && (
                  <button type="button" className="botao-link" onClick={() => aoEstornar(r)} data-teste={`estornar-${r.numero}`}>
                    Estornar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </RolagemIndicada>
  );
}

// ---------------------------------------------------------------- ficha do recebível avulso
type ModalAvulso = 'receber' | 'nota' | 'valor' | 'editar' | 'cancelar' | { estornar: Recebimento } | null;
export function FichaAvulso({ numero }: { numero: string }) {
  const carga = useCarga(() => api.get<Avulso>(`/api/receber/avulsos/${encodeURIComponent(numero)}`), [numero]);
  const [modal, setModal] = useState<ModalAvulso>(null);
  const a = carga.dados;
  if (!a) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ficha-avulso" />;
  const p = a.permissoes;
  const salvo = () => (setModal(null), void carga.recarregar());
  const notaAtiva = a.notas.find((n) => n.situacao !== 'cancelada' && n.situacao !== 'substituida');
  const historico = [
    ...a.valores.map((v, i) => ({ em: v.em, texto: i === 0 ? `Recebível criado: ${rs(v.valor)} (${v.situacao}) · previsão ${dataBr(v.previsao)}` : `Valor/previsão atualizados: ${rs(v.valor)} (${v.situacao}) · previsão ${dataBr(v.previsao)} · ${v.justificativa}`, por: v.por })),
    ...a.recebimentos.flatMap((r) => [{ em: r.registradoEm, texto: `${r.numero}: recebido ${rs(r.valor)} em ${dataBr(r.data)} (${r.conta.nome})`, por: r.por }, ...(r.estorno ? [{ em: r.estorno.em, texto: `${r.numero} estornado · ${r.estorno.justificativa}`, por: r.estorno.por }] : [])]),
    ...a.creditos.map((m) => ({ em: m.em, texto: `Crédito ${m.credito} aplicado: ${rs(m.valor)}${m.desfeito ? ` (desfeito: ${m.desfeito.justificativa})` : ''}`, por: m.por })),
    ...(a.cancelamento ? [{ em: a.cancelamento.em, texto: `Cancelado · ${a.cancelamento.justificativa}`, por: a.cancelamento.por }] : []),
  ].sort((x, y) => y.em.localeCompare(x.em));
  return (
    <>
      <div className="rc-cab">
        <div>
          <h1 className="rc-titulo" data-teste="titulo-avulso">
            {a.descricao}
          </h1>
          <span className="texto-secundario">
            {cod(a.numero)} · {a.pagador?.nome ?? '—'} · {a.tipoRotulo}
          </span>
        </div>
        <Situacao rotulo={SIT_AVULSO[a.situacao].rotulo} tom={SIT_AVULSO[a.situacao].tom} teste="situacao-avulso" />
      </div>
      <div className="rc-topo">
        <div className="rc-caixa" data-teste="origem-avulso">
          <dl className="rc-origem-bloco">
            <div>
              <dt>Origem: lançamento avulso</dt>
              <dd>{a.origem}</dd>
            </div>
            <div>
              <dt>Documentos</dt>
              <dd>{a.anexos.some((x) => !x.retirado) ? <Arquivos anexos={a.anexos} /> : <span className="texto-secundario">nenhum</span>}</dd>
            </div>
          </dl>
        </div>
        <div className="rc-caixa rc-linha-acoes">
          <span>
            <span className="rc-sec">Previsão de recebimento</span>
            <strong>{dataBr(a.previsao)}</strong>
          </span>
          {!a.cancelamento && p.registrarRecebimento && Number(a.saldo) > 0 && !notaAtiva && (
            <button type="button" className="botao-primario" onClick={() => setModal('receber')} data-teste="acao-receber-avulso">
              Registrar recebimento
            </button>
          )}
          {!a.cancelamento && p.registrarAvulso && (
            <button type="button" className="botao-secundario" onClick={() => setModal('editar')}>
              Editar dados
            </button>
          )}
          {!a.cancelamento && (p.registrarAvulso || p.cancelarAvulso) && (
            <details className="rc-menu">
              <summary className="botao-secundario">Ações ▾</summary>
              <div className="rc-menu-lista">
                {p.registrarAvulso && (
                  <button type="button" onClick={() => setModal('valor')} data-teste="acao-valor-avulso">
                    Atualizar valor ou previsão
                  </button>
                )}
                {p.cancelarAvulso && (
                  <button type="button" onClick={() => setModal('cancelar')} data-teste="acao-cancelar-avulso">
                    Cancelar recebível
                  </button>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
      {a.cancelamento && <div className="aviso">Cancelado em {dataHora(a.cancelamento.em)}: {a.cancelamento.justificativa}</div>}
      <div className="rc-cards4">
        <div className="rc-card">
          <b>Previsto</b>
          <strong>{rs(a.valor)}</strong>
          <span className="rc-sec">{a.situacaoValor === 'confirmado' ? 'valor confirmado' : 'valor estimado'}</span>
        </div>
        <div className="rc-card">
          <b>Confirmado</b>
          <strong>{a.situacaoValor === 'confirmado' ? rs(a.valor) : '—'}</strong>
        </div>
        <div className="rc-card">
          <b>Recebido em dinheiro</b>
          <strong data-teste="recebido-avulso">{rs(a.recebido)}</strong>
          <span className="rc-sec">
            quitado {rs(a.quitado)}
            {Number(a.descontos.ir) + Number(a.descontos.antecipacao) + Number(a.creditoAplicado) > 0 ? ` (inclui IR, antecipação ou crédito aplicado)` : ''}
          </span>
        </div>
        <div className="rc-card verde">
          <b>Saldo</b>
          <strong data-teste="saldo-avulso">{rs(a.saldo)}</strong>
        </div>
      </div>
      <div className="rc-caixa rc-linha-acoes" data-teste="documento-fiscal">
        <span>
          <strong>Documento fiscal</strong> {notaAtiva ? <Situacao rotulo={`${notaAtiva.numero} · NF ${notaAtiva.numeroFiscal}`} tom="azul" /> : <span className="selo-previa pend">Pendente</span>}
          <span className="rc-sec">{notaAtiva ? 'O recebimento é registrado na nota.' : 'Registre o documento fiscal quando disponível, conforme o fluxo acordado.'}</span>
        </span>
        {notaAtiva ? (
          <button type="button" className="botao-secundario" onClick={() => navegar(`/contas-receber/notas/${notaAtiva.numero}`)}>
            Ver nota
          </button>
        ) : (
          !a.cancelamento &&
          p.emitirNota && (
            <button type="button" className="botao-secundario" disabled={a.situacaoValor !== 'confirmado'} title={a.situacaoValor !== 'confirmado' ? 'Confirme o valor antes de emitir a nota' : undefined} onClick={() => setModal('nota')} data-teste="acao-nota-avulso">
              Registrar documento
            </button>
          )
        )}
      </div>
      <details className="rc-recolhido" data-teste="historico-avulso">
        <summary>Histórico e documentos</summary>
        <div>
          <TabelaRecebimentos recebimentos={a.recebimentos} podeEstornar={p.estornarRecebimento} aoEstornar={(r) => setModal({ estornar: r })} />
          <ul className="rc-hist">
            {historico.map((h, i) => (
              <li key={i}>
                <span>{dataHora(h.em)}</span>
                <span>
                  {h.texto}
                  {h.por ? ` · ${h.por}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </details>
      <details className="rc-recolhido">
        <summary>Classificação</summary>
        <div>
          <p className="sem-margem">{a.planoConta ?? 'Sem classificação gerencial.'}</p>
          {a.observacao && <p className="texto-secundario">{a.observacao}</p>}
        </div>
      </details>
      {modal === 'receber' && <RegistrarRecebimento alvo={{ avulso: a.numero, rotulo: `${a.numero} · ${a.pagador?.nome ?? ''}` }} devido={a.saldo} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal === 'nota' && <EmitirNota inicial={{ avulso: a.numero }} aoFechar={() => setModal(null)} aoSalvar={(n) => navegar(`/contas-receber/notas/${n}`)} />}
      {modal === 'valor' && <ValorAvulso a={a} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal === 'editar' && <EditarAvulso a={a} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal === 'cancelar' && <Justificar titulo={`Cancelar ${a.numero}`} texto="O recebível fica preservado no histórico, fora dos valores a receber. Recebimentos e notas ativas precisam ser desfeitos antes." rotulo="Cancelar recebível" caminho={`/api/receber/avulsos/${a.numero}/cancelar`} aoFechar={() => setModal(null)} aoSalvar={salvo} teste="cancelar-avulso" />}
      {modal && typeof modal === 'object' && 'estornar' in modal && <Justificar titulo={`Estornar ${modal.estornar.numero}`} texto={`${rs(modal.estornar.valor)} em ${dataBr(modal.estornar.data)}. O registro é preservado; o saldo volta.`} rotulo="Estornar recebimento" caminho={`/api/receber/recebimentos/${modal.estornar.numero}/estornar`} aoFechar={() => setModal(null)} aoSalvar={salvo} teste="estornar-recebimento" />}
    </>
  );
}

function ValorAvulso({ a, aoFechar, aoSalvar }: { a: Avulso; aoFechar: () => void; aoSalvar: () => void }) {
  const [valor, setValor] = useState(a.valor.replace('.', ','));
  const [situacao, setSituacao] = useState(a.situacaoValor);
  const [previsao, setPrevisao] = useState(a.previsao);
  const [just, setJust] = useState('');
  const env = useEnviar(() => aoSalvar());
  return (
    <Modal titulo={`Atualizar valor ou previsão — ${a.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Salvar" pronto={just.trim().length >= 4 && centavosDe(valor) > 0} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', `/api/receber/avulsos/${a.numero}/valor`, { valor: (centavosDe(valor) / 100).toFixed(2), situacao, previsao, justificativa: just })} teste="salvar-valor-avulso" />}>
      <Erro texto={env.erro} />
      <div className="grade-form tres">
        <Campo rotulo="Valor">{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />}</Campo>
        <Campo rotulo="Situação do valor">
          {(id) => (
            <select id={id} value={situacao} onChange={(e) => setSituacao(e.target.value as 'estimado' | 'confirmado')} data-teste="situacao-valor-avulso">
              <option value="estimado">Estimado</option>
              <option value="confirmado">Confirmado com o pagador</option>
            </select>
          )}
        </Campo>
        <CampoData rotulo="Previsão de recebimento" valor={previsao} aoMudar={setPrevisao} />
      </div>
      <Campo rotulo="Justificativa (obrigatória)">{(id) => <input id={id} value={just} maxLength={500} onChange={(e) => setJust(e.target.value)} data-teste="justificativa-valor-avulso" />}</Campo>
      <p className="texto-secundario">O valor anterior fica no histórico. O valor confirmado pode ficar acima ou abaixo do estimado; nenhuma diferença vira crédito automaticamente.</p>
    </Modal>
  );
}

function EditarAvulso({ a, aoFechar, aoSalvar }: { a: Avulso; aoFechar: () => void; aoSalvar: () => void }) {
  const [descricao, setDescricao] = useState(a.descricao);
  const [origem, setOrigem] = useState(a.origem);
  const [observacao, setObservacao] = useState(a.observacao ?? '');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const env = useEnviar(() => aoSalvar());
  const salvar = async () => env.enviar('patch', `/api/receber/avulsos/${a.numero}`, { descricao, origem, observacao: observacao || null, documento: await lerArquivo(arquivo) });
  return (
    <Modal titulo={`Editar dados — ${a.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Salvar" pronto={descricao.trim().length >= 3 && origem.trim().length >= 3} enviando={env.enviando} aoConfirmar={() => void salvar()} teste="salvar-avulso" />}>
      <Erro texto={env.erro} />
      <Campo rotulo="Descrição">{(id) => <input id={id} value={descricao} maxLength={200} onChange={(e) => setDescricao(e.target.value)} />}</Campo>
      <Campo rotulo="Origem">{(id) => <input id={id} value={origem} maxLength={200} onChange={(e) => setOrigem(e.target.value)} />}</Campo>
      <Campo rotulo="Observação">{(id) => <textarea id={id} rows={2} value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} />}</Campo>
      {a.permissoes.enviarDocumento && <Campo rotulo="Novo documento (opcional)">{(id) => <input id={id} type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />}</Campo>}
    </Modal>
  );
}

// ---------------------------------------------------------------- ficha da nota da Inttrix
type ModalNota = 'receber' | 'cancelar' | 'reemitir' | 'anexo' | 'credito' | 'liberar' | { estornar: Recebimento } | null;
export function FichaNota({ numero }: { numero: string }) {
  const carga = useCarga(() => api.get<Nota>(`/api/receber/notas/${encodeURIComponent(numero)}`), [numero]);
  const [modal, setModal] = useState<ModalNota>(null);
  const n = carga.dados;
  if (!n) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ficha-nota" />;
  const p = n.permissoes;
  const ativa = n.situacao !== 'cancelada' && n.situacao !== 'substituida';
  const salvo = () => (setModal(null), void carga.recarregar());
  return (
    <>
      <div className="rc-cab">
        <div>
          <h1 className="rc-titulo" data-teste="titulo-nota">
            {cod(n.numero)} · NF {cod(n.numeroFiscal)}
          </h1>
          <span className="texto-secundario">
            {n.pagador?.nome ?? '—'} · emitida em {dataBr(n.dataEmissao)}
            {n.substitui ? ` · substitui ${n.substitui.numero}` : ''}
          </span>
        </div>
        <div className="acoes-direita">
          <Situacao rotulo={SIT_NOTA[n.situacao].rotulo} tom={SIT_NOTA[n.situacao].tom} teste="situacao-nota" />
          {ativa && p.registrarRecebimento && Number(n.saldo) > 0 && (
            <button type="button" className="botao-primario" onClick={() => setModal('receber')} data-teste="acao-receber-nota">
              Registrar recebimento
            </button>
          )}
          {ativa && (
            <details className="rc-menu">
              <summary className="botao-secundario">Ações ▾</summary>
              <div className="rc-menu-lista">
                {p.gerirCredito && n.creditosDisponiveis.length > 0 && Number(n.saldo) > 0 && (
                  <button type="button" onClick={() => setModal('credito')}>
                    Aplicar crédito do pagador
                  </button>
                )}
                {p.emitirNota && (
                  <button type="button" onClick={() => setModal('anexo')}>
                    Anexar arquivo ou carta de correção
                  </button>
                )}
                {p.emitirNota && p.cancelarNota && (
                  <button type="button" onClick={() => setModal('reemitir')} data-teste="acao-reemitir">
                    Reemitir (substituir)
                  </button>
                )}
                {p.cancelarNota && (
                  <button type="button" onClick={() => setModal('cancelar')} data-teste="acao-cancelar-nota">
                    Cancelar nota
                  </button>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
      {n.cancelamento && (
        <div className="aviso">
          {n.cancelamento.motivo === 'reemissao' ? `Substituída por ${n.substituta?.numero ?? 'nova nota'}` : 'Cancelada'} em {dataHora(n.cancelamento.em)}: {n.cancelamento.justificativa}
          {n.substituta && (
            <>
              {' '}
              <button type="button" className="botao-link" onClick={() => navegar(`/contas-receber/notas/${n.substituta!.numero}`)}>
                abrir a substituta
              </button>
            </>
          )}
        </div>
      )}
      <div className="rc-cards-auto" data-teste="valores-nota">
        <div className="rc-card">
          <b>Valor da nota</b>
          <strong>{rs(n.valor)}</strong>
          <span className="rc-sec">{n.ir ? `IR destacado ${rs(n.ir.valor)} · líquido esperado ${rs(n.liquidoEsperado)}` : 'sem IR destacado'}</span>
        </div>
        <div className="rc-card">
          <b>Recebido em dinheiro</b>
          <strong data-teste="recebido-nota">{rs(n.recebido)}</strong>
          <span className="rc-sec">entradas na conta</span>
        </div>
        <div className="rc-card">
          <b>Retenções e descontos</b>
          <strong>{rs(Number(n.descontos.ir) + Number(n.descontos.antecipacao))}</strong>
          <span className="rc-sec">
            IR {rs(n.descontos.ir)} · antecipação {rs(n.descontos.antecipacao)}
          </span>
        </div>
        <div className="rc-card">
          <b>Crédito aplicado</b>
          <strong>{rs(n.creditoAplicado)}</strong>
          <span className="rc-sec">não é entrada de caixa</span>
        </div>
        <div className="rc-card">
          <b>Quitado</b>
          <strong data-teste="quitado-nota">{rs(n.quitado)}</strong>
          <span className="rc-sec">dinheiro + retenções + crédito</span>
        </div>
        <div className={`rc-card ${Number(n.saldo) > 0 && n.situacao === 'parcial' ? 'amarelo' : 'verde'}`}>
          <b>{n.situacao === 'parcial' ? 'Diferença pendente' : 'Saldo'}</b>
          <strong data-teste="saldo-nota">{rs(n.saldo)}</strong>
          {n.situacao === 'parcial' && <span className="rc-sec">nada é baixado por suposição</span>}
        </div>
      </div>
      {n.situacao === 'cancelada' && (
        <div className={`rc-caixa rc-linha-acoes`} data-teste="itens-nota-cancelada">
          <span>
            <strong>{n.seguraItens ? 'Itens presos a esta nota cancelada' : 'Itens liberados para nova cobrança'}</strong>
            <span className="rc-sec">
              {n.seguraItens ? 'Cancelar o documento não libera os itens: libere-os quando a nova cobrança for decidida.' : `Liberados em ${dataHora(n.liberacaoItens!.em)}${n.liberacaoItens!.por ? ` por ${n.liberacaoItens!.por}` : ''}: ${n.liberacaoItens!.justificativa}`}
              {n.creditoCancelamento && ` Valor recebido ${n.cancelamento?.tratamento === 'devolucao' ? 'devolvido ao pagador' : 'mantido como crédito do pagador'}: ${n.creditoCancelamento.numero} (${rs(n.creditoCancelamento.valor)}, saldo ${rs(n.creditoCancelamento.saldo)}).`}
            </span>
          </span>
          {n.seguraItens && p.cancelarNota && (
            <button type="button" className="botao-secundario" onClick={() => setModal('liberar')} data-teste="acao-liberar-itens">
              Liberar itens para nova cobrança
            </button>
          )}
        </div>
      )}
      <h3 className="rc-subtitulo">Itens</h3>
      <RolagemIndicada className="rolagem quadro-tabela" teste="itens-da-nota">
        <table className="tabela tabela-compacta rc-fixo" data-teste="tabela-itens-nota">
          <thead>
            <tr>
              <th style={{ width: '46%' }}>Item</th>
              <th className="numero" style={{ width: '18%' }}>
                Valor
              </th>
              <th className="numero" style={{ width: '18%' }}>
                Quitado
              </th>
              <th className="numero" style={{ width: '18%' }}>
                Em dinheiro
              </th>
            </tr>
          </thead>
          <tbody>
            {n.itens.map((i) => (
              <tr key={i.id}>
                <td>
                  <a
                    className="rc-codigo"
                    href={`#/contas-receber/${i.origem.startsWith('RAV') ? `avulsos/${i.origem}` : i.origem}`}
                    onClick={(e) => (e.preventDefault(), navegar(`/contas-receber/${i.origem.startsWith('RAV') ? `avulsos/${i.origem}` : i.origem}`))}
                  >
                    {cod(i.origem)}
                  </a>
                  <span className="rc-sec">{cod(i.rotulo)}</span>
                </td>
                <td className="numero">{rs(i.valor)}</td>
                <td className="numero">{rs(i.quitado)}</td>
                <td className="numero">{rs(i.recebidoCaixa)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
      <h3 className="rc-subtitulo">Recebimentos</h3>
      <TabelaRecebimentos recebimentos={n.recebimentos} podeEstornar={p.estornarRecebimento} aoEstornar={(r) => setModal({ estornar: r })} />
      <details className="rc-recolhido">
        <summary>Documentos e créditos aplicados</summary>
        <div>
          <p className="sem-margem">{n.anexos.some((a) => !a.retirado) ? <Arquivos anexos={n.anexos} /> : <span className="texto-secundario">Nenhum arquivo anexado.</span>}</p>
          {n.creditos.length > 0 && (
            <ul className="rc-hist">
              {n.creditos.map((m) => (
                <li key={m.id} className={m.desfeito ? 'anulado' : undefined}>
                  <span>{dataBr(m.data)}</span>
                  <span>
                    {m.credito} aplicado: {rs(m.valor)}
                    {m.desfeito ? ` · desfeito (${m.desfeito.justificativa})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="texto-secundario">Registrada em {dataHora(n.registradaEm)}{n.por ? ` por ${n.por}` : ''}. Receita gerencial: mês de {dataBr(n.dataEmissao).slice(3)}, pelo valor da nota.</p>
        </div>
      </details>
      {modal === 'receber' && <RegistrarRecebimento alvo={{ nota: n.numero, rotulo: `${n.numero} · NF ${n.numeroFiscal} · ${n.pagador?.nome ?? ''}` }} devido={n.saldo} irNota={n.recebimentos.some((r) => !r.estorno) ? null : n.ir} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal === 'cancelar' && <CancelarNota n={n} aoFechar={() => setModal(null)} aoSalvar={salvo} aoReemitir={() => setModal('reemitir')} aoEstornar={(r) => setModal({ estornar: r })} />}
      {modal === 'liberar' && <Justificar titulo={`Liberar itens da ${n.numero}`} texto="Os itens desta nota cancelada voltam a poder ser cobrados numa nova nota. O valor já recebido continua tratado à parte (crédito ou devolução) e o histórico é preservado." rotulo="Liberar itens" caminho={`/api/receber/notas/${n.numero}/liberar-itens`} aoFechar={() => setModal(null)} aoSalvar={salvo} teste="liberar-itens" />}
      {modal === 'reemitir' && <Reemitir n={n} aoFechar={() => setModal(null)} aoSalvar={(num) => navegar(`/contas-receber/notas/${num}`)} />}
      {modal === 'anexo' && <AnexarNota n={n} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal === 'credito' && <AplicarCredito alvo={{ nota: n.numero }} creditos={n.creditosDisponiveis} saldoAlvo={n.saldo} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal && typeof modal === 'object' && 'estornar' in modal && <Justificar titulo={`Estornar ${modal.estornar.numero}`} texto={`${rs(modal.estornar.valor)} em ${dataBr(modal.estornar.data)}. O registro é preservado; o saldo da nota volta.`} rotulo="Estornar recebimento" caminho={`/api/receber/recebimentos/${modal.estornar.numero}/estornar`} aoFechar={() => setModal(null)} aoSalvar={salvo} teste="estornar-recebimento" />}
    </>
  );
}

// ---------------------------------------------------------------- cancelar nota (caminhos separados)
interface PreviaCancelamento {
  pode: boolean;
  recebidoCaixa: string;
  retencoes: { ir: string; antecipacao: string };
  tratamento: string | null;
  itens: { rotulo: string; valor: string; efeito: string }[];
  preservados: string[];
  aviso: string | null;
  providencias: string[];
}
function CancelarNota({ n, aoFechar, aoSalvar, aoReemitir, aoEstornar }: { n: Nota; aoFechar: () => void; aoSalvar: () => void; aoReemitir: () => void; aoEstornar: (r: Recebimento) => void }) {
  const apoio = useApoio();
  const ativos = n.recebimentos.filter((r) => !r.estorno);
  const temRecebido = ativos.length > 0;
  const [tratamento, setTratamento] = useState<'' | 'credito' | 'devolucao'>('');
  const [liberar, setLiberar] = useState(false);
  const [conta, setConta] = useState('');
  const [data, setData] = useState(hojeIso());
  const [identificador, setIdentificador] = useState('');
  const [just, setJust] = useState('');
  const [chave] = useState(novaChave);
  useEffect(() => {
    if (apoio && !conta) setConta(apoio.contas.find((c) => c.principal)?.id ?? apoio.contas[0]?.id ?? '');
  }, [apoio, conta]);
  const corpo = { tratamento: tratamento || undefined, liberar_itens: liberar, devolucao: tratamento === 'devolucao' ? { conta_bancaria_id: conta, data, identificador: identificador || undefined } : undefined };
  const pv = usePrevia<PreviaCancelamento>(`/api/receber/notas/${n.numero}/cancelar`, corpo);
  const env = useEnviar(() => aoSalvar());
  return (
    <Modal
      titulo={`Cancelar ${n.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={<Rodape aoFechar={aoFechar} rotulo="Cancelar nota" pronto={Boolean(pv.previa?.pode) && just.trim().length >= 10} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', `/api/receber/notas/${n.numero}/cancelar`, { ...corpo, justificativa: just, chave_idempotencia: chave })} teste="confirmar-cancelar-nota" />}
    >
      <Erro texto={env.erro} />
      <div className="rc-caminhos" data-teste="caminhos-cancelamento">
        <div>
          <strong>Substituir por outra nota</strong>
          <span className="rc-sec">Cancelamento e reemissão vinculados: os recebimentos passam a contar na nova nota.</span>
          <button type="button" className="botao-link" onClick={aoReemitir}>
            Reemitir
          </button>
        </div>
        {temRecebido && (
          <div>
            <strong>Recebimento registrado por engano?</strong>
            <span className="rc-sec">Estorne o registro incorreto (não é devolução de dinheiro).</span>
            {ativos.map((r) => (
              <button key={r.id} type="button" className="botao-link" onClick={() => aoEstornar(r)}>
                Estornar {r.numero}
              </button>
            ))}
          </div>
        )}
      </div>
      {temRecebido && (
        <fieldset className="rc-secao" data-teste="tratamento-recebido">
          <legend>O valor já recebido ({rs(pv.previa?.recebidoCaixa ?? 0)}) — escolha o tratamento</legend>
          <label className="opcao">
            <input type="radio" name="tratamento" checked={tratamento === 'credito'} onChange={() => setTratamento('credito')} data-teste="tratamento-credito" /> Manter como crédito do pagador (para usar numa próxima nota ou devolver depois)
          </label>
          <label className="opcao">
            <input type="radio" name="tratamento" checked={tratamento === 'devolucao'} onChange={() => setTratamento('devolucao')} data-teste="tratamento-devolucao" /> Devolver ao pagador agora (devolução efetiva)
          </label>
          {tratamento === 'devolucao' && (
            <div className="grade-form tres">
              <Campo rotulo="Conta da devolução">
                {(id) => (
                  <select id={id} value={conta} onChange={(e) => setConta(e.target.value)}>
                    {(apoio?.contas ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <CampoData rotulo="Data da devolução" valor={data} aoMudar={setData} max={hojeIso()} />
              <Campo rotulo="Identificador (opcional)">{(id) => <input id={id} value={identificador} maxLength={120} onChange={(e) => setIdentificador(e.target.value)} />}</Campo>
            </div>
          )}
        </fieldset>
      )}
      <label className="opcao">
        <input type="checkbox" checked={liberar} onChange={(e) => setLiberar(e.target.checked)} data-teste="liberar-itens-agora" /> Liberar agora os itens para nova cobrança (sem marcar, continuam presos a esta nota)
      </label>
      {pv.previa && (
        <div className="rc-secao" data-teste="efeitos-cancelamento">
          <h3>Efeitos</h3>
          <ul className="rc-lista">
            {pv.previa.tratamento && <li>{pv.previa.tratamento}</li>}
            {pv.previa.itens.map((i) => (
              <li key={i.rotulo}>
                {i.rotulo} ({rs(i.valor)}): {i.efeito}
              </li>
            ))}
            <li>Preservados: {pv.previa.preservados.join(', ')}.</li>
            {pv.previa.aviso && <li>{pv.previa.aviso}</li>}
          </ul>
          {pv.previa.providencias.length > 0 && (
            <div className="rc-alerta atencao" role="status" data-teste="providencias">
              <strong>Falta uma providência:</strong> {pv.previa.providencias.join(' ')}
            </div>
          )}
        </div>
      )}
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} data-teste="cancelar-nota-justificativa" />}</Campo>
    </Modal>
  );
}

function Reemitir({ n, aoFechar, aoSalvar }: { n: Nota; aoFechar: () => void; aoSalvar: (numero: string) => void }) {
  const [numeroFiscal, setNumeroFiscal] = useState('');
  const [data, setData] = useState(hojeIso());
  const [just, setJust] = useState('');
  const [chave] = useState(novaChave);
  const env = useEnviar<{ numero: string }>((r) => aoSalvar(r.numero));
  return (
    <Modal titulo={`Reemitir ${n.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Reemitir" pronto={numeroFiscal.trim().length > 0 && just.trim().length >= 10} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', `/api/receber/notas/${n.numero}/reemitir`, { numero_fiscal: numeroFiscal, data_emissao: data, justificativa: just, chave_idempotencia: chave })} teste="confirmar-reemissao" />}>
      <Erro texto={env.erro} />
      <p>A nota atual é cancelada (motivo reemissão) e a nova a substitui, com os mesmos itens e valores. Recebimentos e créditos já aplicados passam a contar na nova nota; nada é apagado.</p>
      <div className="grade-form duas">
        <Campo rotulo="Número da nova nota fiscal">{(id) => <input id={id} value={numeroFiscal} maxLength={40} onChange={(e) => setNumeroFiscal(e.target.value)} data-teste="numero-reemissao" />}</Campo>
        <CampoData rotulo="Data de emissão" valor={data} aoMudar={setData} max={hojeIso()} />
      </div>
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(id) => <textarea id={id} rows={2} value={just} maxLength={500} onChange={(e) => setJust(e.target.value)} data-teste="justificativa-reemissao" />}</Campo>
    </Modal>
  );
}

function AnexarNota({ n, aoFechar, aoSalvar }: { n: Nota; aoFechar: () => void; aoSalvar: () => void }) {
  const [papel, setPapel] = useState<'documento' | 'carta_correcao'>('documento');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const env = useEnviar(() => aoSalvar());
  const salvar = async () => env.enviar('post', `/api/receber/notas/${n.numero}/anexos`, { papel, arquivo: await lerArquivo(arquivo) });
  return (
    <Modal titulo={`Anexar à ${n.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Anexar" pronto={Boolean(arquivo)} enviando={env.enviando} aoConfirmar={() => void salvar()} teste="confirmar-anexo-nota" />}>
      <Erro texto={env.erro} />
      <Campo rotulo="Tipo">
        {(id) => (
          <select id={id} value={papel} onChange={(e) => setPapel(e.target.value as 'documento' | 'carta_correcao')}>
            <option value="documento">Arquivo da nota</option>
            <option value="carta_correcao">Carta de correção</option>
          </select>
        )}
      </Campo>
      <Campo rotulo="Arquivo">{(id) => <input id={id} type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />}</Campo>
      <p className="texto-secundario">A carta de correção é anexada sem mudar valores; correção de valor exige reemissão.</p>
    </Modal>
  );
}

function AplicarCredito({ alvo, creditos, saldoAlvo, aoFechar, aoSalvar }: { alvo: { nota?: string; avulso?: string }; creditos: { numero: string; saldo: string }[]; saldoAlvo: string; aoFechar: () => void; aoSalvar: () => void }) {
  const [credito, setCredito] = useState(creditos[0]?.numero ?? '');
  const saldo = Number(creditos.find((c) => c.numero === credito)?.saldo ?? 0);
  const [valor, setValor] = useState(Math.min(saldo, Number(saldoAlvo)).toFixed(2).replace('.', ','));
  const [chave] = useState(novaChave);
  const env = useEnviar(() => aoSalvar());
  return (
    <Modal titulo="Aplicar crédito do pagador" aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Aplicar" pronto={centavosDe(valor) > 0 && Boolean(credito)} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', `/api/receber/creditos/${credito}/aplicar`, { ...alvo, valor: (centavosDe(valor) / 100).toFixed(2), chave_idempotencia: chave })} teste="confirmar-aplicar-credito" />}>
      <Erro texto={env.erro} />
      <div className="grade-form duas">
        <Campo rotulo="Crédito">
          {(id) => (
            <select id={id} value={credito} onChange={(e) => setCredito(e.target.value)}>
              {creditos.map((c) => (
                <option key={c.numero} value={c.numero}>
                  {c.numero} · saldo {rs(c.saldo)}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Valor">{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />}</Campo>
      </div>
      <p className="texto-secundario">Saldo a receber: {rs(saldoAlvo)}. A aplicação não é entrada de dinheiro e pode ser desfeita com justificativa.</p>
    </Modal>
  );
}

// ---------------------------------------------------------------- listas: notas e créditos
export function TelaNotas() {
  const carga = useCarga(() => api.get<{ notas: { numero: string; numeroFiscal: string; dataEmissao: string; pagador: string; valor: string; liquidoEsperado: string; recebido: string; saldo: string; situacao: Nota['situacao']; itens: number }[] }>('/api/receber/notas'), []);
  const d = carga.dados;
  return (
    <>
      <div className="rc-cab">
        <h1 className="rc-titulo">Notas da Inttrix</h1>
        <button type="button" className="botao-secundario" onClick={() => navegar('/contas-receber')}>
          Voltar ao Contas a Receber
        </button>
      </div>
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : !d.notas.length ? (
        <p className="vazio">Nenhuma nota registrada. Emita pela aba Comissões (itens disponíveis para resgate) ou pela ficha do recebível avulso.</p>
      ) : (
        <RolagemIndicada className="rolagem quadro-tabela" teste="lista-notas">
          <table className="tabela tabela-compacta" data-teste="tabela-notas">
            <thead>
              <tr>
                <th>Nota</th>
                <th>Pagador</th>
                <th>Emissão</th>
                <th className="numero">Valor</th>
                <th className="numero">Recebido</th>
                <th className="numero">Saldo</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {d.notas.map((n) => (
                <tr key={n.numero}>
                  <td>
                    <a className="rc-codigo" href={`#/contas-receber/notas/${n.numero}`} onClick={(e) => (e.preventDefault(), navegar(`/contas-receber/notas/${n.numero}`))}>
                      {cod(n.numero)}
                    </a>
                    <span className="rc-sec">NF {cod(n.numeroFiscal)} · {n.itens} item(ns)</span>
                  </td>
                  <td>{n.pagador}</td>
                  <td>{dataBr(n.dataEmissao)}</td>
                  <td className="numero">{rs(n.valor)}</td>
                  <td className="numero">{rs(n.recebido)}</td>
                  <td className="numero">{rs(n.saldo)}</td>
                  <td>
                    <Situacao rotulo={SIT_NOTA[n.situacao].rotulo} tom={SIT_NOTA[n.situacao].tom} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      )}
    </>
  );
}

interface Credito {
  numero: string;
  pagador: Pagador | null;
  recebimento: string;
  recebimentoEstornado: boolean;
  valor: string;
  saldo: string;
  movimentos: Movimento[];
  em: string;
}
export function TelaCreditos() {
  const carga = useCarga(() => api.get<{ creditos: Credito[]; permissoes: PermissoesReceber }>('/api/receber/creditos'), []);
  const [modal, setModal] = useState<{ devolver: Credito } | { desfazer: Movimento } | null>(null);
  const d = carga.dados;
  const salvo = () => (setModal(null), void carga.recarregar());
  return (
    <>
      <div className="rc-cab">
        <h1 className="rc-titulo">Créditos do pagador</h1>
        <button type="button" className="botao-secundario" onClick={() => navegar('/contas-receber')}>
          Voltar ao Contas a Receber
        </button>
      </div>
      <p className="texto-secundario">Crédito real do pagador (excedente de recebimento registrado por escolha). Aplicar em nota ou recebível do mesmo pagador, devolver ou desfazer. Devoluções de fornecedor continuam no Contas a Pagar.</p>
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      ) : !d.creditos.length ? (
        <p className="vazio">Nenhum crédito do pagador.</p>
      ) : (
        d.creditos.map((c) => (
          <div key={c.numero} className="rc-caixa" data-teste={`credito-${c.numero}`}>
            <div className="rc-linha-acoes">
              <span>
                <strong>{c.numero}</strong> · {c.pagador?.nome ?? '—'} · origem {c.recebimento.startsWith('NFI') ? `valor recebido da ${c.recebimento} (cancelada)` : `excedente do ${c.recebimento}`}
                {c.recebimentoEstornado && <span className="rc-sec rc-alerta-txt">recebimento de origem estornado</span>}
              </span>
              <span>
                valor {rs(c.valor)} · <strong>saldo {rs(c.saldo)}</strong>
              </span>
              {d.permissoes.gerirCredito && Number(c.saldo) > 0 && (
                <button type="button" className="botao-secundario" onClick={() => setModal({ devolver: c })}>
                  Devolver
                </button>
              )}
            </div>
            {c.movimentos.length > 0 && (
              <ul className="rc-hist">
                {c.movimentos.map((m) => (
                  <li key={m.id} className={m.desfeito ? 'anulado' : undefined}>
                    <span>{dataBr(m.data)}</span>
                    <span>
                      {m.tipo === 'aplicacao' ? `Aplicado em ${m.alvo}` : `Devolvido (${m.conta})`}: {rs(m.valor)}
                      {m.desfeito ? ` · desfeito (${m.desfeito.justificativa})` : ''}
                      {!m.desfeito && d.permissoes.gerirCredito && (
                        <>
                          {' '}
                          <button type="button" className="botao-link" onClick={() => setModal({ desfazer: m })}>
                            Desfazer
                          </button>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
      {modal && 'devolver' in modal && <DevolverCredito c={modal.devolver} aoFechar={() => setModal(null)} aoSalvar={salvo} />}
      {modal && 'desfazer' in modal && <Justificar titulo={`Desfazer movimento do ${modal.desfazer.credito}`} texto={`${modal.desfazer.tipo === 'aplicacao' ? 'Aplicação' : 'Devolução'} de ${rs(modal.desfazer.valor)} em ${dataBr(modal.desfazer.data)}. O registro é preservado e o saldo do crédito volta.`} rotulo="Desfazer" caminho={`/api/receber/creditos/movimentos/${modal.desfazer.id}/desfazer`} aoFechar={() => setModal(null)} aoSalvar={salvo} teste="desfazer-credito" />}
    </>
  );
}

function DevolverCredito({ c, aoFechar, aoSalvar }: { c: Credito; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const [conta, setConta] = useState('');
  const [data, setData] = useState(hojeIso());
  const [valor, setValor] = useState(Number(c.saldo).toFixed(2).replace('.', ','));
  const [chave] = useState(novaChave);
  const env = useEnviar(() => aoSalvar());
  useEffect(() => {
    if (apoio && !conta) setConta(apoio.contas.find((x) => x.principal)?.id ?? apoio.contas[0]?.id ?? '');
  }, [apoio, conta]);
  return (
    <Modal titulo={`Devolver crédito ${c.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Registrar devolução" pronto={Boolean(conta) && centavosDe(valor) > 0} enviando={env.enviando} aoConfirmar={() => void env.enviar('post', `/api/receber/creditos/${c.numero}/devolver`, { conta_bancaria_id: conta, data, valor: (centavosDe(valor) / 100).toFixed(2), chave_idempotencia: chave })} teste="confirmar-devolucao" />}>
      <Erro texto={env.erro} />
      <div className="grade-form tres">
        <Campo rotulo="Conta bancária">
          {(id) => (
            <select id={id} value={conta} onChange={(e) => setConta(e.target.value)}>
              {(apoio?.contas ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nome}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <CampoData rotulo="Data da devolução" valor={data} aoMudar={setData} max={hojeIso()} />
        <Campo rotulo="Valor">{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />}</Campo>
      </div>
    </Modal>
  );
}
